import { useMemo } from "react";
import { get } from "lodash";
import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import {
  getUsersTableColumns,
  getUsersTableFilters,
} from "routes/admin/components/UsersTable/UsersTableColumnsAndFilters";
import RoleService from "services/Role";
import RegionService from "services/Regions";
import UserService from "services/User";
import CustomFieldService from "services/CustomField";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";
import { USERS_TABLE_CONSTANTS } from "routes/admin/components/UsersTable/UsersTableConstants";

export const invalidateUsersQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [USERS_TABLE_CONSTANTS.queryKey],
  });
};

const handleFetchRoles = async () => {
  const roles = await RoleService.getRoles();
  return roles;
};

const handleFetchRegions = async () => {
  const rawRegions = await RegionService.getRegions();
  const regions = rawRegions.map(({ id, name, countries = [] }) => ({
    id,
    name,
    extraData: { countries },
  }));
  return regions;
};

const handleFetchCustomFields = async () => {
  const customFields = await UserService.getCustomFields();
  const configs = await CustomFieldService.getCustomFieldConfigsForModel(
    USERS_TABLE_CONSTANTS.className
  );
  return [
    {
      accessorPrefix: "custom_fields",
      fields: customFields,
      model: USERS_TABLE_CONSTANTS.className,
      headerPrefix: "Users",
      configs,
    },
  ];
};

// Other hooks cache a { regions, countries } object under ["regions"], so the
// array returned by handleFetchRegions needs its own key. The ["regions"]
// prefix keeps it covered by the Regions table's invalidation.
const USER_REGIONS_QUERY_KEY = ["regions", USERS_TABLE_CONSTANTS.className];

// Shares the table's query keys and fetchers so the form and the filters read
// the same cache entries.
export const useUserFormOptions = () => ({
  roles: useQuery({ queryKey: ["roles"], queryFn: handleFetchRoles }),
  regions: useQuery({
    queryKey: USER_REGIONS_QUERY_KEY,
    queryFn: handleFetchRegions,
  }),
});

export const useUserMutations = () => {
  const create = useMutation({
    mutationFn: data => UserService.createUser(data),
    onSuccess: () => invalidateUsersQuery(),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => UserService.updateUser(id, data),
    onSuccess: () => invalidateUsersQuery(),
  });

  const updateRoles = useMutation({
    mutationFn: ({ userIds, role }) =>
      Promise.all(
        userIds.map(id => UserService.updateUser(id, { rolesOnly: true, role }))
      ),
    onSuccess: () => invalidateUsersQuery(),
  });

  return { create, update, updateRoles };
};

export const useUsersTable = ({
  canEditUser = false,
  canDeleteUser = false,
  canResetPassword = false,
  canDeactivateUser = false,
  onEditUser = () => {},
  onDeleteUser = () => {},
  onResetPassword = () => {},
  onDeactivateUser = () => {},
  onReactivateUser = () => {},
  onForceLogout = () => {},
}) => {
  const { data: roles, isSuccess: isRolesFetched } = useQuery({
    queryKey: ["roles"],
    queryFn: handleFetchRoles,
  });

  const { data: regions, isSuccess: isRegionsFetched } = useQuery({
    queryKey: USER_REGIONS_QUERY_KEY,
    queryFn: handleFetchRegions,
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", USERS_TABLE_CONSTANTS.className],
    queryFn: handleFetchCustomFields,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (!isRolesFetched || !isRegionsFetched || !isCustomFieldsFetched) {
      return [];
    }

    const finalFilters = getUsersTableFilters({ roles, regions });

    if (isCustomFieldsFetched) {
      finalFilters.push(
        ...buildCustomFieldFilters(
          customFields,
          finalFilters.map(f => f.title)
        )
      );
    }

    return finalFilters;
  }, [
    roles,
    regions,
    customFields,
    isRolesFetched,
    isRegionsFetched,
    isCustomFieldsFetched,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];

    const initialColumns = getUsersTableColumns({
      canEditUser,
      canDeleteUser,
      canResetPassword,
      canDeactivateUser,
      onEditUser,
      onDeleteUser,
      onResetPassword,
      onDeactivateUser,
      onReactivateUser,
      onForceLogout,
    });

    const usersTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${USERS_TABLE_CONSTANTS.tableName}`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: usersTableSettings?.visible_columns || [],
    });

    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: usersTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    customFields,
    tableSettings,
    isTableSettingsFetched,
    isCustomFieldsFetched,
    canEditUser,
    canDeleteUser,
    canResetPassword,
    canDeactivateUser,
    onEditUser,
    onDeleteUser,
    onResetPassword,
    onDeactivateUser,
    onReactivateUser,
    onForceLogout,
  ]);

  return {
    filters,
    columns,
    customFields,
    roles,
    regions,
  };
};
